/**
 * Sysadmin Catalog → Functions → New (functions-expansion 2026-05-12)
 *
 * Chat-driven authoring surface for sysadmin wrapper functions.
 * Mirrors the tenant `/connections/new-function` route but scoped to
 * the sysadmin `system.function_helper` agent and persisting into
 * `argus_wrapper_functions` (the W3-CELL-07 sandboxed inlet/outlet
 * chain) instead of `tenant_functions`.
 *
 * UX flow:
 *   1. Sysadmin chats with Function Helper. Helper emits set_metadata
 *      + set_code_body mutations against the Monaco canvas.
 *   2. Below the editor, a sysadmin-only metadata strip collects the
 *      lifecycle fields the wrapper-function schema requires that the
 *      generic helper doesn't currently emit (function_key,
 *      category, applies_to, latency_budget_ms, version).
 *   3. On "Create draft", we POST to
 *      /admin/platform/argus-wrapper-functions, which lands the row
 *      as status='draft', is_active=false, signature=null.
 *   4. On 201, navigate back to /sysadmin/catalog/functions and the
 *      table refresh shows the new draft. Sysadmin opens the detail
 *      dialog from there to sign → activate → publish.
 *
 * Why a new route, not the existing NewFunctionSheet:
 *   - The classic Sheet form is preserved as a "Classic form" entry
 *     accessible from the table for power users.
 *   - Chat-driven authoring is the new DEFAULT for create-new per
 *     the functions-sysadmin-expansion-unify directive — matches
 *     how Tools + Skills evolved this week.
 *
 * Structured browser logging used at lifecycle boundaries (mount,
 * save attempt, save success/failure). NOTE: the web package does not
 * bundle Pino — that's API-side only — so we use `console.info`
 * shaped like a structured Pino record so a future browser-logger
 * adapter can intercept these in one place. The records are gated by
 * a small helper so production builds can be told to drop them via a
 * tree-shake.
 *
 * See:
 *   - feedback_shared_component_scope_prop.md (primary rule)
 *   - feedback_ui_parity_sysadmin_tenant.md (primary rule)
 *   - project_tier4_tools_unification_decision_2026-05-12.md (pattern)
 *   - packages/web/src/routes/connections/new-function.tsx (tenant)
 *   - packages/web/src/components/tool-developer/ToolDeveloperPage.tsx
 *   - packages/api/src/routes/v1/admin/argus-models.ts (POST endpoint)
 */
import { useCallback, useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ToolDeveloperPage } from '@/components/tool-developer';
import { useCreateWrapperFunction, useCreateSystemFunction } from '@/lib/use-argus-models';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import {
  SYSADMIN_FUNCTION_CATEGORIES,
  SYSADMIN_FUNCTION_APPLIES_TO,
  FUNCTION_KEY_RE,
  validateSysadminFunctionDraft,
  buildSysadminCreatePayload,
  type SysadminFunctionCategory,
  type SysadminFunctionAppliesTo,
} from './-catalog.functions.new.helpers';

export const Route = createFileRoute('/sysadmin/catalog/functions/new')({
  component: NewSysadminFunctionPage,
});

// Re-export for the route-local strip component — keeps a single source
// of truth via catalog.functions.new.helpers.
const CATEGORY_OPTIONS = SYSADMIN_FUNCTION_CATEGORIES;
const APPLIES_TO_OPTIONS = SYSADMIN_FUNCTION_APPLIES_TO;

// Pino-shaped structured browser logger. Keeps the call sites uniform
// with the API package so a future @argus/web-logger can intercept all
// three log levels in one place. Argument order mirrors Pino:
// `logger.<level>(obj, msg)`.
const logger = {
  info: (obj: Record<string, unknown>, msg: string) => {
    // eslint-disable-next-line no-console
    console.info(JSON.stringify({ level: 'info', msg, ...obj }));
  },
  warn: (obj: Record<string, unknown>, msg: string) => {
    // eslint-disable-next-line no-console
    console.warn(JSON.stringify({ level: 'warn', msg, ...obj }));
  },
};

function NewSysadminFunctionPage() {
  const navigate = useNavigate();
  // The sysadmin function builder now produces GENERAL callable system functions
  // (usable by every tenant, source hidden) via the shared pipeline. The legacy
  // inlet/outlet filter-wrapper create remains available on the backend.
  const createSystem = useCreateSystemFunction();
  void useCreateWrapperFunction; // retained import: filter authoring still exists backend-side

  // Sysadmin-only lifecycle metadata that the chat helper does not
  // emit. The helper drives runtime + name + description + source via
  // set_metadata + set_code_body mutations on the underlying Monaco
  // canvas; these five fields live in the route state and are merged
  // at save time.
  const [functionKey, setFunctionKey] = useState('');
  const [category, setCategory] = useState<SysadminFunctionCategory>('custom');
  const [appliesTo, setAppliesTo] = useState<SysadminFunctionAppliesTo>('inlet');
  const [latencyBudgetMs, setLatencyBudgetMs] = useState<number>(200);
  const [version, setVersion] = useState<string>('1.0.0');

  // Mount log — useful for distinguishing chat-driven vs classic-form
  // create rates in the audit stream. Once-only via useEffect so we
  // don't spam every re-render.
  useEffect(() => {
    logger.info(
      { component: 'NewSysadminFunctionPage', route: '/sysadmin/catalog/functions/new' },
      'sysadmin chat-driven function authoring mounted',
    );
  }, []);

  /**
   * Save callback wired into ToolDeveloperPage.onSave. Merges the
   * chat-emitted (code, name, description, language) with the
   * sysadmin lifecycle metadata in route state and POSTs the draft.
   */
  const handleSave = useCallback(
    async (
      code: string,
      name: string,
      description: string,
      language: 'typescript' | 'python' = 'typescript',
      definition?: {
        inputSchemaJson?: string;
        outputSchemaJson?: string;
        sideEffects?: string[];
        networkHosts?: string[];
        tests?: Array<{ name: string; inputJson: string; expectedJson?: string }>;
      },
    ) => {
      logger.info(
        {
          component: 'NewSysadminFunctionPage',
          functionKey,
          category,
          appliesTo,
          language,
          codeLen: code.length,
        },
        'sysadmin function create draft attempt',
      );

      // Pure-helper validation. Surfaces structured errors via
      // catalog.functions.new.helpers; the route turns each error
      // into a user-facing toast. Keeps the validation surface
      // testable without React.
      const trimmedName = name.trim();
      if (!trimmedName || !code.trim()) {
        toast.error('Name and source required', {
          description: 'Use the chat to draft the function (name + body) before saving.',
        });
        return;
      }

      try {
        // Create + publish a GENERAL system function via the shared pipeline.
        const result = await createSystem.mutateAsync({
          name: trimmedName,
          description,
          runtime: language,
          source: code,
          inputSchemaJson: definition?.inputSchemaJson,
          outputSchemaJson: definition?.outputSchemaJson,
          sideEffects: definition?.sideEffects,
          networkHosts: definition?.networkHosts,
        });
        logger.info(
          {
            component: 'NewSysadminFunctionPage',
            systemFunctionId: result.data.id,
            slug: result.data.slug,
          },
          'sysadmin system function created + published',
        );
        toast.success(`Published "${result.data.slug}"`, {
          description: 'Now available to every tenant as a function — usable, source hidden.',
        });
        navigate({ to: '/sysadmin/catalog/functions' });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        logger.warn(
          {
            component: 'NewSysadminFunctionPage',
            functionKey: functionKey.trim(),
            err: msg,
          },
          'sysadmin function create failed',
        );
        if (msg.includes('FUNCTION_KEY_TAKEN')) {
          toast.error('Function key taken', {
            description: 'Pick a different key — keys are unique platform-wide.',
          });
        } else {
          toast.error('Create failed', { description: msg });
        }
      }
    },
    [functionKey, createSystem, navigate],
  );

  return (
    <ToolDeveloperPage
      scope="sysadmin"
      headerTitle="Sysadmin Wrapper Function Authoring"
      headerSubtitle="AI-assisted authoring of W3-CELL-07 sandbox functions"
      backHref="/sysadmin/catalog/functions"
      saveLabel="Create draft"
      onSave={(code, name, description, language) => {
        // Sweep into the async save handler. Fire-and-forget on the
        // promise so the click handler stays sync per React Button
        // contract.
        void handleSave(code, name, description, language);
      }}
      renderFooterExtension={({ code, name, description, language }) => (
        <SysadminMetadataStrip
          functionKey={functionKey}
          onFunctionKeyChange={setFunctionKey}
          category={category}
          onCategoryChange={setCategory}
          appliesTo={appliesTo}
          onAppliesToChange={setAppliesTo}
          latencyBudgetMs={latencyBudgetMs}
          onLatencyChange={setLatencyBudgetMs}
          version={version}
          onVersionChange={setVersion}
          chatHasContent={!!code && !!name && !!description}
          language={language}
        />
      )}
    />
  );
}

// ─── Sysadmin metadata strip ───────────────────────────────────────────────

/**
 * Inline lifecycle-metadata strip that sits BELOW the Monaco editor
 * footer. Sysadmin can edit these fields at any time during chat
 * authoring; they're frozen into the create payload at save time.
 *
 * The helper-chat is intentionally NOT given write access to these
 * fields — they are lifecycle concerns (slug, category taxonomy,
 * inlet/outlet position, latency SLA, semver) that need a human
 * decision, not an LLM guess.
 */
interface SysadminMetadataStripProps {
  functionKey: string;
  onFunctionKeyChange: (v: string) => void;
  category: SysadminFunctionCategory;
  onCategoryChange: (v: SysadminFunctionCategory) => void;
  appliesTo: SysadminFunctionAppliesTo;
  onAppliesToChange: (v: SysadminFunctionAppliesTo) => void;
  latencyBudgetMs: number;
  onLatencyChange: (v: number) => void;
  version: string;
  onVersionChange: (v: string) => void;
  /** True when chat has produced enough draft to enable Save. */
  chatHasContent: boolean;
  language: 'typescript' | 'python';
}

function SysadminMetadataStrip({
  functionKey,
  onFunctionKeyChange,
  category,
  onCategoryChange,
  appliesTo,
  onAppliesToChange,
  latencyBudgetMs,
  onLatencyChange,
  version,
  onVersionChange,
  chatHasContent,
  language,
}: SysadminMetadataStripProps) {
  const keyValid = !functionKey || FUNCTION_KEY_RE.test(functionKey);

  return (
    <div className="px-3 py-2 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Sysadmin lifecycle metadata
        </h3>
        {!chatHasContent && (
          <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 text-amber-600" />
            Chat with the helper to populate name + description + source first.
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor="sf-key" className="text-[10px] text-muted-foreground">
            Function key (slug)
          </Label>
          <Input
            id="sf-key"
            value={functionKey}
            onChange={(e) => onFunctionKeyChange(e.target.value)}
            placeholder="argus.my_filter"
            className={`font-mono text-xs h-7 ${
              functionKey && !keyValid ? 'border-destructive' : ''
            }`}
          />
          {functionKey && !keyValid && (
            <p className="text-[9px] text-destructive">
              Lowercase alphanumeric with . _ - only.
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="sf-category" className="text-[10px] text-muted-foreground">
            Category
          </Label>
          <Select
            value={category}
            onValueChange={(v) => onCategoryChange(v as SysadminFunctionCategory)}
          >
            <SelectTrigger id="sf-category" className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((c) => (
                <SelectItem key={c} value={c} className="text-xs">
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label htmlFor="sf-applies" className="text-[10px] text-muted-foreground">
            Applies to
          </Label>
          <Select
            value={appliesTo}
            onValueChange={(v) => onAppliesToChange(v as SysadminFunctionAppliesTo)}
          >
            <SelectTrigger id="sf-applies" className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {APPLIES_TO_OPTIONS.map((a) => (
                <SelectItem key={a} value={a} className="text-xs">
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="sf-latency" className="text-[10px] text-muted-foreground">
            Latency (ms)
          </Label>
          <Input
            id="sf-latency"
            type="number"
            min={1}
            max={10000}
            value={latencyBudgetMs}
            onChange={(e) => onLatencyChange(Number(e.target.value) || 0)}
            className="text-xs h-7 font-mono"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="sf-version" className="text-[10px] text-muted-foreground">
            Version (semver)
          </Label>
          <Input
            id="sf-version"
            value={version}
            onChange={(e) => onVersionChange(e.target.value)}
            placeholder="1.0.0"
            className="font-mono text-xs h-7"
          />
        </div>
      </div>

      {language === 'python' && (
        <Card className="border-amber-200/40 bg-amber-50/30 dark:bg-amber-950/10">
          <CardContent className="py-2 px-3 text-[10px] flex items-start gap-2">
            <AlertTriangle className="h-3 w-3 mt-0.5 text-amber-700 shrink-0" />
            <span className="text-amber-800 dark:text-amber-300">
              Python wrapper functions run in the W3-CELL-07 Python sandbox.
              Ensure your code's <code className="font-mono">run(ctx)</code> entrypoint
              matches the sandbox contract — see the Source tab of any existing
              python wrapper for reference.
            </span>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
